import React, { useState, useEffect } from "react";
import PageHero from "../components/PageHero";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowUpRight, RotateCcw, Box, Cylinder, Square } from "lucide-react";
import { getSettings } from "../api";

const SHAPES = [
  {id:"slab", name:"Slab", I:Square},
  {id:"column", name:"Column", I:Cylinder},
  {id:"beam", name:"Beam", I:Box}
];

export default function DIY() {
  const [settings, setSettings] = useState({});
  const [shape, setShape] = useState("slab");
  const [grade, setGrade] = useState("M20");
  const [dims, setDims] = useState({ l:"", w:"", h:"", d:"" });
  const [result, setResult] = useState(null);

  useEffect(() => { getSettings().then(setSettings); }, []);

  const calc = () => {
    const l = parseFloat(dims.l)||0, w = parseFloat(dims.w)||0, h = parseFloat(dims.h)||0, d = parseFloat(dims.d)||0;
    let vol = 0;
    if (shape === "slab") vol = l * w * (h/1000);
    else if (shape === "column") vol = Math.PI * Math.pow(d/2000, 2) * h;
    else if (shape === "beam") vol = l * (w/1000) * (h/1000);
    const wet = vol * 1.54;
    setResult({ vol: vol.toFixed(3), cement: (wet*320).toFixed(0), sand: (wet*0.45).toFixed(2), agg: (wet*0.9).toFixed(2), water: (wet*160).toFixed(0) });
  };
  const reset = () => { setDims({l:"",w:"",h:"",d:""}); setResult(null); };

  return (
    <main>
      <PageHero title="Calculator" subtitle={settings.diy_hero_subtitle || "Estimate concrete volume and material requirements for your project in seconds."} num="04"/>

      <section className="py-20 bg-[#fafafa]">
        <div className="max-w-[1300px] mx-auto px-5 md:px-8">
          <div className="mb-10">
            <p className="font-mono text-[11px] text-[#ef2435] tracking-widest mb-5 font-semibold">[ STEP_01 / SELECT_SHAPE ]</p>
            <div className="grid grid-cols-3 gap-3">
              {SHAPES.map(s => (
                <button key={s.id} onClick={()=>{setShape(s.id); setResult(null)}} className={`rounded-2xl p-6 text-left transition-all ${shape===s.id ? 'bg-[#ef2435] border border-[#ef2435] text-white glow-red' : 'card-dark'}`}>
                  <s.I size={28} className={shape===s.id?"text-white mb-4":"text-[#ef2435] mb-4"}/>
                  <p className={`font-display text-2xl ${shape===s.id?'text-white':'text-gray-900'}`}>{s.name.toUpperCase()}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-[1fr,1.1fr] gap-6">
            <div className="card-dark rounded-3xl p-8 md:p-10">
              <div className="flex items-center justify-between mb-8">
                <p className="font-mono text-[11px] text-[#ef2435] tracking-widest font-semibold">[ STEP_02 / DIMENSIONS ]</p>
                <button onClick={reset} className="text-gray-500 hover:text-[#ef2435] flex items-center gap-1.5 text-xs"><RotateCcw size={12}/> Reset</button>
              </div>
              <div className="space-y-5">
                {shape !== "column" && (<>
                  <Field label="Length" unit="m"><Input type="number" value={dims.l} onChange={e=>setDims({...dims,l:e.target.value})} className="input-light" placeholder="6.0"/></Field>
                  <Field label="Width" unit={shape==="slab"?"m":"mm"}><Input type="number" value={dims.w} onChange={e=>setDims({...dims,w:e.target.value})} className="input-light" placeholder={shape==="slab"?"4.0":"230"}/></Field>
                  <Field label={shape==="slab"?"Thickness":"Depth"} unit="mm"><Input type="number" value={dims.h} onChange={e=>setDims({...dims,h:e.target.value})} className="input-light" placeholder="150"/></Field>
                </>)}
                {shape === "column" && (<>
                  <Field label="Diameter" unit="mm"><Input type="number" value={dims.d} onChange={e=>setDims({...dims,d:e.target.value})} className="input-light" placeholder="300"/></Field>
                  <Field label="Height" unit="m"><Input type="number" value={dims.h} onChange={e=>setDims({...dims,h:e.target.value})} className="input-light" placeholder="3.0"/></Field>
                </>)}
                <Field label="Concrete Grade">
                  <Select value={grade} onValueChange={setGrade}>
                    <SelectTrigger className="input-light"><SelectValue/></SelectTrigger>
                    <SelectContent>{['M15','M20','M25','M30','M35','M40'].map(g=><SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Button onClick={calc} className="btn-primary w-full h-14 rounded-full text-sm font-semibold mt-3">Calculate volume <ArrowUpRight size={16} className="ml-2"/></Button>
              </div>
            </div>

            <div className="rounded-3xl p-8 md:p-10 bg-white border border-[#ececef] relative overflow-hidden shadow-sm">
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-15 blur-3xl" style={{background:'radial-gradient(circle, #ef2435, transparent 70%)'}}/>
              <div className="relative">
                <p className="font-mono text-[11px] text-[#ef2435] tracking-widest mb-5 font-semibold">[ STEP_03 / RESULT ]</p>
                {!result ? (
                  <div className="py-24 text-center">
                    <p className="giant-text text-7xl mb-3">0.000</p>
                    <p className="text-gray-500 text-sm">Fill in the dimensions and tap Calculate</p>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-500 text-xs uppercase tracking-widest font-mono mb-3">CONCRETE VOLUME</p>
                    <p className="giant-text text-7xl md:text-8xl mb-2">{result.vol}</p>
                    <p className="text-[#ef2435] font-mono text-sm mb-8 font-semibold">m³ · GRADE {grade}</p>
                    <div className="space-y-3">
                      {[["Cement",`${result.cement} kg`],["Sand",`${result.sand} m³`],["Aggregate",`${result.agg} m³`],["Water",`${result.water} L`]].map(([l,v])=>(
                        <div key={l} className="flex items-center justify-between py-3 border-b border-[#ececef]">
                          <span className="text-gray-600 text-sm">{l}</span>
                          <span className="font-mono text-gray-900 font-semibold">~ {v}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-6 font-mono">*ESTIMATES BASED ON STANDARD M-GRADE PROPORTIONS.</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <style>{`.input-light { background: #fafafa; border-color: #ececef; color: #18181c; height: 48px; } .input-light:focus { border-color: #ef2435 !important; }`}</style>
    </main>
  );
}

function Field({label, unit, children}) {
  return (
    <div>
      <div className="flex justify-between mb-2">
        <Label className="text-gray-500 text-xs uppercase tracking-widest font-mono">{label}</Label>
        {unit && <span className="text-[#ef2435] text-xs font-mono font-semibold">[{unit}]</span>}
      </div>
      {children}
    </div>
  );
}
